import { useMemo } from 'react'

import useQuery from '@api/hooks/useQuery'
import { BasketStats } from '@domain'
import { faPiggyBank } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material'
import { BarChart } from '@mui/x-charts-pro'
import MarketBasketReportsApi, { MarketBasketReportsKeys } from '@services/marketBasketReports.api'
import { largeNumToString, numToCurrency } from '@utils/stringHelpers'
import dayjs from 'dayjs'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

import { buildDateRange, dateRanges, toDailySavingsDataset } from './Charts.utils'

export default function TotalDailySavings() {
	const [range, setRange] = useQueryParam(
		'total-daily-savings-range',
		withDefault(StringParam, '7-days')
	)

	const { dateRangeStart, dateRangeEnd } = useMemo(() => buildDateRange(range), [range])

	const { data, isLoading } = useQuery({
		queryKey: MarketBasketReportsKeys.basketStats({ dateRangeStart, dateRangeEnd }),
		queryFn: () => MarketBasketReportsApi.basketStats({ dateRangeStart, dateRangeEnd }),
	})

	return (
		<AnnualSavingsUI data={data || []} isLoading={isLoading} range={range} setRange={setRange} />
	)
}

type AnnualSavingsUIProps = {
	data: BasketStats[]
	isLoading?: boolean
	range: string
	setRange: (range: string) => void
}

export function AnnualSavingsUI({
	data,
	isLoading = false,
	range,
	setRange,
}: AnnualSavingsUIProps) {
	const theme = useTheme()
	const dataset = data ? toDailySavingsDataset(data) : []

	// TODO: currency hardcoded to USD for now
	const currency = 'USD'

	return (
		<Paper elevation={1} sx={{ width: '100%' }}>
			<Stack p={2} width="100%" direction="row" justifyContent="space-between" alignItems="center">
				<Stack direction="row" spacing={2} p="1rem" alignItems="center">
					<FontAwesomeIcon icon={faPiggyBank} size="lg" />
					<Typography variant="h6">Total Daily Savings</Typography>
					<Typography variant="body2" color="text.secondary">
						in basket
					</Typography>
				</Stack>
				<FormControl sx={{ width: '13.75rem' }}>
					<InputLabel>Range</InputLabel>
					<Select
						data-testid="range-select"
						value={range}
						label="Range"
						onChange={(val) => setRange(val.target.value)}
					>
						{dateRanges.map((range) => (
							<MenuItem key={range.value} value={range.value}>
								{range.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Stack>
			{isLoading || !data ? (
				<Skeleton variant="rectangular" width="100%" height={300} />
			) : (
				<BarChart
					dataset={dataset}
					xAxis={[
						{
							dataKey: 'date',
							scaleType: 'band',
							valueFormatter: (v) => dayjs(v as Date).format('MMM D'),
						},
					]}
					yAxis={[
						{
							width: 50,
							valueFormatter: (v) => largeNumToString(v),
						},
					]}
					series={[
						{
							id: 'annualPriceSavings',
							dataKey: 'annualPriceSavings',
							label: 'Annual Price Savings',
							color: theme.palette.primary.main,
							valueFormatter: (v) => numToCurrency(v, currency),
							stack: 'savings',
							stackOrder: 'reverse',
						},
						{
							id: 'annualRebateSavings',
							dataKey: 'annualRebateSavings',
							label: 'Annual Rebate Savings',
							color: theme.palette.secondary.main,
							valueFormatter: (v) => numToCurrency(v, currency),
							stack: 'savings',
							stackOrder: 'reverse',
						},
					]}
					borderRadius={8}
					height={500}
					slotProps={{
						legend: {
							position: { vertical: 'bottom' },
							sx: { mb: 2 },
						},
					}}
					grid={{ horizontal: true }}
				/>
			)}
		</Paper>
	)
}
